import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './EditProfile.css';

const GuestEditProfile = ({ onBack, token, userData, onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    country: '',
    state: '',
    city: '',
  });
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        country: userData.country || '',
        state: userData.state || '',
        city: userData.city || '',
      });
      if (userData.profile_image) {
        setImagePreview(
          userData.profile_image.startsWith('http') 
            ? userData.profile_image 
            : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/${userData.profile_image}`
        );
      }
    }
  }, [userData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/updateProfile`;
      console.log('Updating profile at:', apiUrl);

      let response;
      
      if (profileImage) {
        // Use FormData for file upload
        const formDataToSend = new FormData();
        formDataToSend.append('first_name', formData.first_name);
        formDataToSend.append('last_name', formData.last_name);
        formDataToSend.append('country', formData.country);
        formDataToSend.append('state', formData.state);
        formDataToSend.append('city', formData.city);
        formDataToSend.append('profile_image', profileImage);

        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: formDataToSend,
        });
      } else {
        // Use JSON for text-only updates
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify({
            first_name: formData.first_name,
            last_name: formData.last_name,
            country: formData.country,
            state: formData.state,
            city: formData.city,
          }),
        });
      }

      const data = await response.json();
      console.log('Update response:', data);

      if (response.ok) {
        Swal.fire({
          title: 'Success!',
          text: 'Profile updated successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        }).then(() => {
          if (onProfileUpdate) {
            const updatedProfile = {
              ...formData,
              ...(data.data || data),
            };
            onProfileUpdate(updatedProfile);
          }
          onBack();
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to update profile',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to update profile',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-profile-container">
      <div className="edit-profile-header">
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
        <h2>Edit Profile</h2>
      </div>

      <form className="edit-profile-form" onSubmit={handleSubmit}>
        {/* Profile Image */}
        <div className="profile-image-section">
          <div className="profile-image-preview">
            {imagePreview ? (
              <img src={imagePreview} alt="Profile" />
            ) : (
              <div className="profile-placeholder">👤</div>
            )}
          </div>
          <label className="image-upload-button">
            Change Photo
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              hidden
            />
          </label>
        </div>

        {/* Form Fields */}
        <div className="form-group">
          <label>First Name</label>
          <input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            placeholder="Enter first name"
          />
        </div>

        <div className="form-group">
          <label>Last Name</label>
          <input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            placeholder="Enter last name"
          />
        </div>

        <div className="form-group">
          <label>Country</label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleChange}
            placeholder="Enter country"
          />
        </div>

        <div className="form-group">
          <label>State</label>
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={handleChange}
            placeholder="Enter state"
          />
        </div>

        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="Enter city"
          />
        </div>

        <button 
          type="submit" 
          className="submit-button"
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update Profile'}
        </button>
      </form>
    </div>
  );
};

export default GuestEditProfile;
